import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { searchAll, type SearchKind } from '../lib/api'
import { Empty, Icon, PageHead, Skel } from '../components/ui'

const ORDER: SearchKind[] = ['finding', 'document', 'case', 'engagement']

const KIND_LABEL: Record<SearchKind, string> = {
  finding: 'ข้อสังเกต',
  document: 'เอกสาร',
  case: 'เคส',
  engagement: 'โครงการ',
}

export function Search() {
  const [params, setParams] = useSearchParams()
  const q = (params.get('q') ?? '').trim()
  const [term, setTerm] = useState(q)
  const input = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => { setTerm(q) }, [q])

  const active = q.length >= 2
  const { data, isFetching, error } = useQuery({
    queryKey: ['search', q, 8],
    queryFn: () => searchAll(q, 8),
    enabled: active,
    staleTime: 15_000,
  })

  const groups = [...(data?.groups ?? [])].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind))

  function submit(e?: FormEvent) {
    e?.preventDefault()
    const next = term.trim()
    setParams(next ? { q: next } : {})
  }

  function clear() {
    setTerm('')
    setParams({})
    input.current?.focus()
  }

  return (
    <>
      <PageHead
        title="ค้นหา"
        sub={active && data ? `พบ ${data.total} รายการสำหรับ “${q}”` : 'ค้นหาข้อสังเกต เอกสาร เคส และโครงการจากที่เดียว'}
      />

      <form
        onSubmit={submit}
        className="card"
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 8px 8px 18px', marginBottom: 26 }}
      >
        <span style={{ display: 'flex', color: 'var(--color-ivory-dim)' }} aria-hidden="true">
          <Icon name="search" size={20} />
        </span>
        <input
          ref={input}
          className="input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              clear()
            }
          }}
          placeholder="ค้นหาชื่อข้อสังเกต เอกสาร เคส หรือรหัสโครงการ"
          aria-label="ค้นหาทั้งระบบ"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          style={{ flex: 1, height: 52, fontSize: 17, border: 'none', background: 'transparent', padding: 0 }}
        />
        {term ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clear} aria-label="ล้างคำค้น">
            ล้าง
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={term.trim().length === 0}>
          ค้นหา
        </button>
      </form>

      {!active ? (
        <div className="card">
          <Empty
            title={q ? 'คำค้นสั้นเกินไป' : 'เริ่มค้นหา'}
            body={q ? 'พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหาข้อสังเกต เอกสาร เคส และโครงการ' : 'พิมพ์คำค้นแล้วกด Enter หรือปุ่มค้นหา รองรับทั้งภาษาไทยและภาษาอังกฤษ'}
          />
        </div>
      ) : isFetching ? (
        <div className="stack" aria-busy="true">
          <Skel h={18} w={180} />
          <Skel h={54} />
          <Skel h={54} />
          <Skel h={54} />
          <Skel h={18} w={140} />
          <Skel h={54} />
        </div>
      ) : error ? (
        <div className="card">
          <Empty title="ค้นหาไม่สำเร็จ" body={error instanceof Error ? error.message : 'กรุณาลองใหม่อีกครั้ง'} />
        </div>
      ) : groups.length === 0 ? (
        <div className="card">
          <Empty title="ไม่พบผลลัพธ์" body={`ไม่พบข้อมูลที่ตรงกับ “${q}” ลองใช้คำที่สั้นลงหรือตรวจสอบการสะกด`} />
        </div>
      ) : (
        <div className="stack" style={{ gap: 30 }}>
          {groups.map((g) => (
            <section key={g.kind}>
              <div className="row" style={{ marginBottom: 10 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 21 }}>{g.label || KIND_LABEL[g.kind]}</h2>
                <span className="chip" style={{ marginLeft: 'auto' }}>{g.items.length} รายการ</span>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>รายการ</th>
                      <th style={{ width: 120, textAlign: 'right' }}>ประเภท</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={`${it.kind}-${it.id}`} onClick={() => navigate(it.href)}>
                        <td>
                          <Link to={it.href} style={{ color: 'var(--color-ivory)' }}>{it.title}</Link>
                          {it.subtitle ? (
                            <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{it.subtitle}</div>
                          ) : null}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className="chip">{KIND_LABEL[it.kind]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          <div className="dim" style={{ fontSize: 11.5 }}>
            กด Esc เพื่อล้างคำค้น · Enter เพื่อค้นหาใหม่
          </div>
        </div>
      )}
    </>
  )
}
